import DeleteIcon from '@material-ui/icons/Delete';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import EditIcon from 'components/Icons/EditIcon';
import GenericList from 'components/List/GenericList';
import { EntityColumnType } from 'components/List/TableCellWithConfigurableStyle';
import { useProductionsWithFilter } from 'hooks';
import { Routes } from 'routes';
import { TableFilterContext } from 'tableFilterContext';
import { AppContext } from 'context';
import { useLatestClosureDate } from 'utils/closureHooks';
import { checkDateUpperTimeInterval, createUTCDateFromDateString } from 'utils/date';
import ActionButton from 'components/Buttons/ActionButton';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteProduction } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { ProductionFilters, UserRole } from 'types';
import { generateNotifyFromViolations, isSubmissionErrorType } from 'services/errors';
import { ProductionFilterBar } from './ProductionFilterBar';

const columns = [
  {
    columnPath: '',
    headerTranslationKey: 'productions.action',
    type: 'action' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '5%',
    },
  },
  {
    columnPath: 'material.shortName',
    headerTranslationKey: 'productions.material',
    style: {
      columnWidth: '15%',
    },
  },
  {
    columnPath: 'productionDate',
    headerTranslationKey: 'productions.productionDate',
    type: 'date' as EntityColumnType,
    style: {
      columnWidth: '20%',
      paddingLeft: '3rem',
    },
  },
  {
    columnPath: 'productionDate',
    headerTranslationKey: 'productions.productionTime',
    type: 'hour' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '12%',
    },
  },
  {
    columnPath: 'conditioningQuantity',
    headerTranslationKey: 'productions.conditioningQuantity',
    type: 'number' as EntityColumnType,
    style: {
      columnWidth: '18%',
    },
  },
  {
    columnPath: 'weight',
    headerTranslationKey: 'productions.weight',
    type: 'quantity' as EntityColumnType,
    style: {
      columnWidth: '20%',
      paddingRight: '7rem',
    },
  },
];

type ProductionListProps = {
  filter: ProductionFilters | null;
  setFilter: (filter: ProductionFilters | null) => void;
  setExportDisabled: (isExportDisabled: boolean) => void;
};

const ProductionList: React.FC<ProductionListProps> = ({ filter, setFilter, setExportDisabled }) => {
  const { page, itemsPerPage, order, orderBy } = useContext(TableFilterContext);
  const location = useLocation();
  const history = useHistory();

  const [productionList, total] = useProductionsWithFilter({ page, itemsPerPage }, { order, orderBy }, filter ?? {});

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const minStartDate = sortingCenter?.initializationDate ? new Date(sortingCenter.initializationDate) : new Date();

  const { t } = useTranslation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));

  useEffect(() => {
    if (productionList && productionList.length > 0) {
      setExportDisabled(false);
      return;
    }
    setExportDisabled(true);
  }, [productionList, setExportDisabled]);

  return (
    <>
      <DeleteConfirmationDialog
        title={t('productionForm.deletion.confirm')}
        isOpen={Boolean(confirmDeleteId)}
        setOpen={() => setConfirmDeleteId(null)}
        confirmDeletion={async () => {
          if (!confirmDeleteId) return;
          setConfirmDeleteId(null);
          try {
            await deleteProduction(confirmDeleteId.toString());
            notify({
              message: t('productionForm.deletion.success'),
              severity: 'success',
            });
          } catch (e: any) {
            if (isSubmissionErrorType(e) && e.status === 412 && e.response.body.violations) {
              generateNotifyFromViolations(e.response.body.violations, t);
            } else {
              notify({
                message: t('errorGeneric'),
                severity: 'error',
              });
            }
          }
        }}
      />
      <div style={{ marginTop: 30 }}>
        <ProductionFilterBar
          onChange={setFilter}
          minStartDate={minStartDate}
          maxEndDate={new Date()}
          defaultStartDate={minStartDate}
          defaultEndDate={new Date()}
        />
      </div>
      <GenericList
        entities={productionList}
        columns={columns}
        total={total}
        detailsLink={Routes.Productions}
        generateAvailableActions={({ id, productionDate }) => {
          const productionDatetime = createUTCDateFromDateString(productionDate);
          const disabled = readOnly || checkDateUpperTimeInterval(productionDatetime, closureMonth);
          const linkTo = { pathname: `/productions/${id}/edit`, state: { referer: location.pathname } };

          return [
            {
              title: t('update'),
              onClick: () => history.push(linkTo.pathname, linkTo.state),
              disabled,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={EditIcon}
                  component={Link}
                  data-qa-id={'edit-button'}
                  to={linkTo}
                  disabled={disabled}
                />
              ),
            },
            {
              title: t('delete'),
              onClick: () => setConfirmDeleteId(id),
              disabled,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={DeleteIcon}
                  data-qa-id={'delete-button'}
                  onClick={() => setConfirmDeleteId(id)}
                  disabled={disabled}
                />
              ),
            },
          ];
        }}
      />
    </>
  );
};
export default ProductionList;
